import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Switch from 'components/atoms/Switch'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim, find, isObject, toLower, includes } from 'lodash'

const AddModelModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [marketSegments, setMarketSegments] = useState([])
  const [formData, setFormData] = useState({
    modal: '',
    modalAr: '',
    marketSegment: '',
    active: true,
  })

  useEffect(() => {
    fetchMarketSegments()
    if (id) {
      fetchModelData()
    }
  }, [id])

  const fetchMarketSegments = async (searchQuery = '') => {
    try {
      const response = await apiGet('market-segments', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data?.marketSegment) {
        setMarketSegments(
          response.data.marketSegment
            .filter(item => item.active !== false)
            .map(item => ({
              id: item._id,
              name: isArabic && item.marketSegmentAr ? item.marketSegmentAr : item.marketSegment,
              enName: item.marketSegment,
              arName: item.marketSegmentAr || '',
            }))
        )
      } else {
        setMarketSegments([])
      }
    } catch (error) {
      console.error('Error fetching market segments:', error)
      setMarketSegments([])
    }
  }

  const fetchModelData = async () => {
    try {
      const response = await apiGet(`modal/${id}`)
      if (response.success) {
        const data = response.data
        const marketSegmentId = data.marketSegment?._id || data.marketSegment || ''
        setFormData({
          modal: data.modal || '',
          modalAr: data.modalAr || '',
          marketSegment: marketSegmentId,
          active: data.active !== undefined ? data.active : true,
        })

        if (data.marketSegment && isObject(data.marketSegment)) {
          const option = {
            id: data.marketSegment._id,
            name: isArabic && data.marketSegment.marketSegmentAr ? data.marketSegment.marketSegmentAr : data.marketSegment.marketSegment,
            enName: data.marketSegment.marketSegment,
            arName: data.marketSegment.marketSegmentAr || '',
          }
          setMarketSegments(prev => (find(prev, { id: option.id }) ? prev : [option, ...prev]))
        }
      } else {
        notify.error(t('addModelModal.messages.failedToFetchModelData'))
      }
    } catch (error) {
      console.error('Error fetching model data:', error)
      notify.error(t('addModelModal.messages.failedToFetchModelData'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleMarketSegmentChange = selectedValue => {
    setFormData(prevData => ({
      ...prevData,
      marketSegment: selectedValue?.id || '',
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.modal)) {
      notify.error(t('addModelModal.messages.modelNameRequired'))
      return
    }
    if (!trim(formData.modalAr)) {
      notify.error(t('addModelModal.messages.modelArabicNameRequired'))
      return
    }
    if (!formData.marketSegment) {
      notify.error(t('addModelModal.messages.marketSegmentRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`modal/${id}`, formData)
        if (response.success) {
          notify.success(t('addModelModal.messages.modelUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('modal', formData)
        if (response.success) {
          notify.success(t('addModelModal.messages.modelAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) {
          customClose()
        }
        close()
      } else {
        const apiMessage = response.message || ''
        if (includes(toLower(apiMessage), 'arabic')) {
          notify.error(t('addModelModal.messages.modelArabicAlreadyExists'))
        } else if (includes(toLower(apiMessage), 'already exists')) {
          notify.error(t('addModelModal.messages.modelAlreadyExists'))
        } else {
          notify.error(apiMessage || t('addModelModal.messages.operationFailed'))
        }
      }
    } catch (error) {
      console.error('Error saving model:', error)
      notify.error(t('addModelModal.messages.failedToSaveModel'))
    }
  }

  const handleCancel = () => {
    if (customClose) {
      customClose()
    }
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="modal" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addModelModal.labels.vehicleModel')}
        </label>
        <input
          type="text"
          name="modal"
          value={formData.modal}
          onChange={handleChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addModelModal.placeholders.enterVehicleModel')}
        />
      </div>
      <div>
        <label htmlFor="modalAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addModelModal.labels.vehicleArabicModel')}
        </label>
        <input
          type="text"
          name="modalAr"
          value={formData.modalAr}
          onChange={handleChange}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addModelModal.placeholders.enterVehicleModelArabic')}
        />
      </div>

      <div>
        <label htmlFor="marketSegment" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addModelModal.labels.marketSegment')}
        </label>
        <Selection
          onChange={handleMarketSegmentChange}
          title={t('addModelModal.placeholders.selectMarketSegment')}
          list={marketSegments}
          value={formData.marketSegment}
          showSearch
          onSearch={searchQuery => fetchMarketSegments(searchQuery)}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addModelModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addModelModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addModelModal.buttons.updateModel') : t('addModelModal.buttons.addModel')}
        </button>
      </div>
    </div>
  )
}

export default AddModelModal
